import React, {useEffect, useState} from 'react';
import axios from '../AxiosInstance';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowUpRightFromSquare, faChevronDown, faChevronUp, faChevronLeft, faChevronRight, faPlus, faSearch} from "@fortawesome/free-solid-svg-icons";
import styles from './styles/operations_sidebar.module.css';
import {SelectInput, TextInput} from "./input/inputs";
import Modal from "./modal/modal";
import {useTranslation} from "react-i18next";

export default function OperationsSidebar({addVariable, parentFetched, handleDeletion, project, layouts, setCurrentLayout, nodes, addResultVariable}) {
    const {t} = useTranslation();
    const [machines, setMachines] = useState([]);
    const [filteredMachines, setFilteredMachines] = useState([]);
    const [allDevices, setAllDevices] = useState([]);
    const [fetched, setFetched] = useState(false);
    const [expandedMachines, setExpandedMachines] = useState({});
    const [expandedDevices, setExpandedDevices] = useState({});
    const [searchFilter, setSearchFilter] = useState('');
    const [form, setForm] = useState({});
    const [showLayoutModal, setShowLayoutModal] = useState(false);
    const operation_group_id = parseInt(localStorage.getItem('operation_group_id'));
    const [allResultVariables, setAllResultVariables] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (!mobile) setSidebarOpen(true);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch machines (device_users) and devices
    useEffect(() => {
        if (project?.id && !fetched) {
            Promise.all([
                axios.get(`device_user/?project_id=${project.id}`),
                axios.get(`device/?project_id=${project.id}`)
            ]).then(([machinesRes, devicesRes]) => {
                // Filter only machines (not people)
                const machineList = (machinesRes.data || []).filter(m => !m.is_human && !m.deleted_at);
                setMachines(machineList);
                setFilteredMachines(machineList);
                setAllDevices(devicesRes.data || []);
            });
            setFetched(true);
        }
    }, [project?.id, fetched]);

    useEffect(() => {
        if(!layouts || layouts.length === 0) return;
        const firstLayoutId = layouts[0]?.id;
        setForm((prevForm) => ({
            ...prevForm,
            layout: firstLayoutId,
            group_id: operation_group_id
        }));
    }, [layouts, operation_group_id]);

    // Filter machines by search
    useEffect(() => {
        if (searchFilter.length > 0) {
            const lower = searchFilter.toLowerCase();
            setFilteredMachines(machines.filter(m => m.name.toLowerCase().includes(lower)));
        } else {
            setFilteredMachines(machines);
        }
    }, [searchFilter, machines]);

    const deleteCallback = (id) => {
        // Re-fetch to restore deleted variable nodes
        if (project?.id) {
            axios.get(`device/?project_id=${project.id}`).then((response) => {
                setAllDevices(response.data || []);
            });
        }
    }

    useEffect(() => {
        deleteCallback(handleDeletion)
    }, [handleDeletion]);

    // Fetch all result variables across all layouts in the group
    useEffect(() => {
        if (operation_group_id) {
            axios.get(`operation_result_variables/?group_id=${operation_group_id}`)
                .then(res => setAllResultVariables(res.data || []))
                .catch(() => setAllResultVariables([]));
        }
    }, [operation_group_id]);

    // Sync currentLayout when form.layout changes
    useEffect(() => {
        if (form.layout) {
            setCurrentLayout(form.layout);
        }
    }, [form.layout, setCurrentLayout])

    const goToSource = (layoutId) => {
        setForm(prev => ({...prev, layout: layoutId}));
    };

    const createLayout = () => {
        axios.post('operation_layout/', form).then((response) => {
            window.location.reload();
        });
    }

    // Group devices by machine (device_user)
    const getDevicesForMachine = (machineId) => {
        return allDevices.filter(d => d.device_user === machineId);
    };

    return (
        <>
        {isMobile && (
            <div
                onClick={() => setSidebarOpen(o => !o)}
                style={{
                    position: 'fixed',
                    left: sidebarOpen ? '280px' : '0',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '28px',
                    height: '48px',
                    backgroundColor: '#4F46E5',
                    color: '#FFF',
                    borderRadius: '0 8px 8px 0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    zIndex: 200,
                    transition: 'left 0.25s ease',
                }}
            >
                <FontAwesomeIcon icon={sidebarOpen ? faChevronLeft : faChevronRight} size="sm"/>
            </div>
        )}
        <div className={`${styles.sidebar_container} d-flex flex-column align-items-start px-3${isMobile && !sidebarOpen ? ' ' + styles.sidebar_collapsed : ''}`}>
            <Modal show={showLayoutModal} setter={setShowLayoutModal} name={t('operations.createGroup')} buttonName={t('common.create')} onClick={() => createLayout()}>
                <TextInput form={form} setForm={setForm} name={'name'}/>
            </Modal>
            <div className={'mt-3'} style={{textAlign: "left", width: '100%'}}>
                <div className={'d-flex justify-content-between align-items-center mb-2'}>
                    <div className={'fs-5'}>{t('operations.sidebar.selectGroup')}</div>
                    <div
                        role={'button'}
                        style={{border: '#ccc 1px solid', borderRadius: '6px'}}
                        className={'px-2 py-1'}
                        onClick={() => {
                            setShowLayoutModal(true);
                        }}
                    >{t('common.new', {name: ''}).trim()}</div>
                </div>
                <div style={{width: '100%'}}>
                    <SelectInput options={layouts} form={form} setForm={setForm} name={'layout'} internalName={'layout'} hideName={true}/>
                </div>
            </div>
            <div className="fw-bold mt-3 mb-2 fs-4 font">{t('operations.sidebar.machinesOf', {name: project.name})}</div>
            <div className="mb-3 w-100 position-relative">
                <input placeholder={t('operations.sidebar.searchMachine')} className={styles.search_input} onChange={(event) => setSearchFilter(event.target.value)}/>
                <FontAwesomeIcon icon={faSearch} className={styles.search_icon}/>
            </div>
            <div className="d-flex flex-column align-items-center w-100">
                {filteredMachines?.map((machine) => {
                    const machineDevices = getDevicesForMachine(machine.id);
                    return (
                        <React.Fragment key={machine.id}>
                            {/* Machine level */}
                            <div className={`${styles.sensor}`} style={{backgroundColor: '#f1f5f9', fontWeight: 600}} onClick={() => {
                                setExpandedMachines((prev) => ({...prev, [machine.id]: !prev[machine.id]}));
                            }}>
                                <div>{machine.name}</div>
                                <div className="d-flex align-items-center gap-2">
                                    <span style={{fontSize: '11px', color: '#94a3b8', fontWeight: 400}}>{t('operations.sidebar.devicesCount', {count: machineDevices.length})}</span>
                                    <FontAwesomeIcon icon={expandedMachines[machine.id] ? faChevronUp : faChevronDown} className="pt-1 pe-1"/>
                                </div>
                            </div>

                            {/* Device level */}
                            {expandedMachines[machine.id] && machineDevices.map((device) => (
                                <React.Fragment key={device.id}>
                                    <div
                                        className={`${styles.sensor}`}
                                        style={{paddingLeft: '24px', fontSize: '13px'}}
                                        onClick={() => {
                                            setExpandedDevices((prev) => ({...prev, [device.id]: !prev[device.id]}));
                                        }}
                                    >
                                        <div>{device.name}</div>
                                        <FontAwesomeIcon icon={expandedDevices[device.id] ? faChevronUp : faChevronDown} className="pt-1 pe-1" size="xs"/>
                                    </div>

                                    {/* Variable level */}
                                    {expandedDevices[device.id] && device.variables?.map((variable) => (
                                        <div key={variable.id} className={`${styles.sensor_item}`} style={{paddingLeft: '40px'}}>
                                            <div className="fw-bold">{variable.variable?.name}</div>
                                            <div onClick={() => addVariable(variable)} className={`${styles.sensor_add}`}>
                                                <FontAwesomeIcon icon={faPlus} size="sm"/>
                                            </div>
                                        </div>
                                    ))}
                                </React.Fragment>
                            ))}
                        </React.Fragment>
                    );
                })}
            </div>

            {/* Result variables from all layouts in the group */}
            {allResultVariables.length > 0 && (
                <>
                    <div className="fw-bold mt-4 mb-2 fs-5" style={{color: '#722ED1'}}>{t('operations.sidebar.resultVariables')}</div>
                    <div className="d-flex flex-column align-items-center w-100">
                        {allResultVariables.map((rv) => (
                            <div key={rv.id} className={`${styles.sensor_item}`} style={{borderColor: '#D3ADF7', backgroundColor: '#F9F0FF'}}>
                                <div>
                                    <div className="fw-bold" style={{color: '#722ED1'}}>{rv.name}</div>
                                    {rv.layout_id !== form.layout && (
                                        <div style={{fontSize: '10px', color: '#9CA3AF'}}>{t('operations.sidebar.from', {name: rv.layout_name})}</div>
                                    )}
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    {rv.layout_id !== form.layout && (
                                        <div onClick={() => goToSource(rv.layout_id)} title={t('operations.sidebar.goToSource')}
                                             role="button"
                                             style={{cursor: 'pointer', color: '#722ED1', fontSize: '12px'}}>
                                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} size="sm"/>
                                        </div>
                                    )}
                                    <div onClick={() => addResultVariable(rv)} className={styles.result_var_add}>
                                        <FontAwesomeIcon icon={faPlus} size="sm"/>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
        </>
    )
}
